import getStore from '../store/store';
import { mutator } from 'satcheljs';
import { ObservableMap } from 'mobx';
import {
    initializeCalendarsCacheInStore,
    addToCalendarsCache,
    removeCalendarFromCalendarsCache,
} from '../actions/publicActions';
import { removeCalendarWithIDFromCalendarsCache } from '../actions/internalActions';
import { getFolderIdByCalendarID } from '../selectors/calendarsCacheSelectors';

type KeyValuePair<T> = [string, T];

function convertObjectToInitializationValues<T>(obj: { [key: string]: T }): KeyValuePair<T>[] {
    const val = Object.keys(obj).map<KeyValuePair<T>>(key => {
        const val = obj[key];

        return [key, val];
    });

    return val;
}

mutator(initializeCalendarsCacheInStore, actionMessage => {
    const {
        defaultCalendarEntry,
        calendarEntries,
        calendarFolders,
        folderIdMapping,
        calendarIdToChangeKeyMapping,
        calendarGroups,
    } = actionMessage;

    const store = getStore();

    if (defaultCalendarEntry) {
        store.defaultCalendars.set(
            defaultCalendarEntry.id.mailboxInfo.mailboxId,
            defaultCalendarEntry
        );
    }

    if (!store.calendarEntryMapping) {
        store.calendarEntryMapping = new ObservableMap();
    }

    convertObjectToInitializationValues(calendarEntries).forEach(([key, value]) =>
        store.calendarEntryMapping.set(key, value)
    );

    if (!store.calendarFolderMapping) {
        store.calendarFolderMapping = new ObservableMap();
    }

    convertObjectToInitializationValues(calendarFolders).forEach(([key, value]) =>
        store.calendarFolderMapping.set(key, value)
    );

    if (!store.folderIdToCalendarId) {
        store.folderIdToCalendarId = new ObservableMap();
    }

    convertObjectToInitializationValues(folderIdMapping).forEach(([key, value]) =>
        store.folderIdToCalendarId.set(key, value)
    );

    if (!store.calendarItemIdToChangeKey) {
        store.calendarItemIdToChangeKey = new ObservableMap();
    }

    convertObjectToInitializationValues(calendarIdToChangeKeyMapping).forEach(([key, value]) =>
        store.calendarItemIdToChangeKey.set(key, value)
    );

    if (!store.calendarGroupsMapping) {
        store.calendarGroupsMapping = new ObservableMap();
    }

    convertObjectToInitializationValues(calendarGroups).forEach(([key, value]) =>
        store.calendarGroupsMapping.set(key, value)
    );

    store.validEntryMapping = new ObservableMap();
});

mutator(addToCalendarsCache, actionMessage => {
    const { calendarEntry, folder, mailboxInfo } = actionMessage;

    const store = getStore();

    store.calendarItemIdToChangeKey.set(calendarEntry.ItemId.Id, calendarEntry.ItemId.ChangeKey);

    let calendarEntryEx = {
        ...calendarEntry,
        id: { id: calendarEntry.ItemId.Id, mailboxInfo: mailboxInfo },
    };
    store.calendarEntryMapping.set(calendarEntry.ItemId.Id, calendarEntryEx);
    store.calendarFolderMapping.set(calendarEntry.ItemId.Id, folder);

    // When we don't have the proper FolderId for the Group Mailbox or linked calendar, we will read the DistinguishedFolderId.
    if (folder.FolderId) {
        store.folderIdToCalendarId.set(folder.FolderId.Id, {
            id: calendarEntry.ItemId.Id,
            mailboxInfo: mailboxInfo,
        });
    } else if (folder.DistinguishedFolderId) {
        store.folderIdToCalendarId.set(folder.DistinguishedFolderId, {
            id: calendarEntry.ItemId.Id,
            mailboxInfo: mailboxInfo,
        });
    }
});

mutator(removeCalendarFromCalendarsCache, actionMessage => {
    const { calendar } = actionMessage;
    const calendarId = calendar.id.id;
    const folderId = calendar.calculatedFolderId ? calendar.calculatedFolderId : null;
    removeCalendarWithId(calendarId, folderId);
});

mutator(removeCalendarWithIDFromCalendarsCache, actionMessage => {
    const { calendarId } = actionMessage;
    const folderId = getFolderIdByCalendarID(calendarId);
    removeCalendarWithId(calendarId, folderId);
});

function removeCalendarWithId(calendarId: string, folderId?: string) {
    const store = getStore();

    store.calendarItemIdToChangeKey.delete(calendarId);
    store.calendarEntryMapping.delete(calendarId);
    store.calendarFolderMapping.delete(calendarId);
    if (folderId) {
        store.folderIdToCalendarId.delete(folderId);
    }
}
